'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Search, Bell, Sun, Moon, ChevronRight, Command, AlertTriangle, X, Menu } from 'lucide-react';
import RoleSwitcher from './RoleSwitcher';

const BREADCRUMB_MAP: Record<string, string> = {
  '/executive-dashboard': 'Executive Dashboard',
  '/vendor-management': 'Vendor Management',
  '/due-diligence-workspace': 'Due Diligence Workspace',
};

const QUICK_ACTIONS = [
  { key: 'qa-dd', label: 'Due Diligence Workspace', href: '/due-diligence-workspace', icon: '🛡️' },
  { key: 'qa-vendors', label: 'Vendor Management', href: '/vendor-management', icon: '🏢' },
  { key: 'qa-dashboard', label: 'Executive Dashboard', href: '/executive-dashboard', icon: '📊' },
];

const NOTIFICATIONS = [
  { id: 'notif-001', type: 'critical', title: 'Insurance Lapse — Iteris Inc.', time: '2h ago', read: false },
  { id: 'notif-002', type: 'warning', title: 'Contract Expiring — NorthStar Billing (32 days)', time: '4h ago', read: false },
  { id: 'notif-003', type: 'warning', title: 'DD Overdue — Granicus LLC', time: '1d ago', read: false },
  { id: 'notif-004', type: 'info', title: 'Tyler Munis renewal review initiated', time: '2d ago', read: true },
];

interface TopbarProps {
  theme: 'light' | 'dark';
  onThemeToggle: () => void;
  onMenuOpen?: () => void;
}

export default function Topbar({ theme, onThemeToggle, onMenuOpen }: TopbarProps) {
  const pathname = usePathname();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [notifOpen, setNotifOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);

  const unreadCount = NOTIFICATIONS.filter((n) => !n.read).length;

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
        setTimeout(() => searchRef.current?.focus(), 50);
      }
      if (e.key === 'Escape') {
        setSearchOpen(false);
        setNotifOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const segments = pathname.split('/').filter(Boolean);
  const breadcrumbs = [
    { key: 'bc-home', label: 'City of Pleasanton', href: '/executive-dashboard' },
    ...segments.map((seg, i) => ({
      key: `bc-${seg}`,
      label: BREADCRUMB_MAP[`/${seg}`] || seg.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
      href: '/' + segments.slice(0, i + 1).join('/'),
    })),
  ];

  return (
    <>
      <header className="h-14 flex items-center gap-3 px-4 border-b border-[hsl(var(--border))] bg-[hsl(var(--surface))] flex-shrink-0 z-30">
        {/* Hamburger — mobile only */}
        <button
          onClick={onMenuOpen}
          className="lg:hidden p-2 rounded-md hover:bg-[hsl(var(--background))] text-[hsl(var(--text-secondary))] transition-colors flex-shrink-0"
          aria-label="Open navigation menu"
        >
          <Menu size={18} />
        </button>

        {/* Breadcrumb */}
        <nav className="flex items-center gap-1 flex-1 min-w-0" aria-label="Breadcrumb">
          {breadcrumbs.map((bc, idx) => (
            <React.Fragment key={bc.key}>
              {idx > 0 && <ChevronRight size={12} className="text-[hsl(var(--text-muted))] flex-shrink-0" />}
              {idx === breadcrumbs.length - 1 ? (
                <span className="text-xs font-medium text-[hsl(var(--text-primary))] truncate">
                  {bc.label}
                </span>
              ) : (
                <Link
                  href={bc.href}
                  className="text-xs text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text-primary))] transition-colors truncate"
                >
                  {bc.label}
                </Link>
              )}
            </React.Fragment>
          ))}
        </nav>

        {/* Search trigger */}
        <button
          onClick={() => { setSearchOpen(true); setTimeout(() => searchRef.current?.focus(), 50); }}
          className="flex items-center gap-2 px-2.5 py-1.5 text-xs rounded-md border border-[hsl(var(--border))] text-[hsl(var(--text-muted))] hover:border-[hsl(var(--border-strong))] hover:text-[hsl(var(--text-secondary))] transition-all duration-150 bg-[hsl(var(--background))] w-52"
        >
          <Search size={12} />
          <span className="flex-1 text-left">Search vendors, contracts…</span>
          <span className="flex items-center gap-0.5 text-2xs">
            <Command size={10} /><span>K</span>
          </span>
        </button>

        {/* Role Switcher */}
        <RoleSwitcher />

        {/* Notifications */}
        <div className="relative" ref={notifRef}>
          <button
            onClick={() => setNotifOpen(!notifOpen)}
            className="relative p-2 rounded-md hover:bg-[hsl(var(--background))] text-[hsl(var(--text-secondary))] transition-colors"
            aria-label={`Notifications — ${unreadCount} unread`}
          >
            <Bell size={16} />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500" />
            )}
          </button>
          {notifOpen && (
            <div className="absolute right-0 top-full mt-1 w-80 card shadow-md z-50 animate-slide-up">
              <div className="flex items-center justify-between px-3 py-2 border-b border-[hsl(var(--border))]">
                <span className="text-xs font-semibold text-[hsl(var(--text-primary))]">
                  Notifications
                </span>
                <span className="badge badge-pending text-2xs">{unreadCount} new</span>
              </div>
              <ul className="divide-y divide-[hsl(var(--border))] max-h-72 overflow-y-auto">
                {NOTIFICATIONS.map((n) => (
                  <li key={n.id} className={`px-3 py-2.5 hover:bg-[hsl(var(--background))] cursor-pointer ${!n.read ? 'bg-[hsl(var(--accent-light))]' : ''}`}>
                    <div className="flex items-start gap-2">
                      <span className={`mt-0.5 flex-shrink-0 ${n.type === 'critical' ? 'text-red-500' : n.type === 'warning' ? 'text-amber-500' : 'text-blue-500'}`}>
                        <AlertTriangle size={12} />
                      </span>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-[hsl(var(--text-primary))] leading-snug">{n.title}</p>
                        <p className="text-2xs text-[hsl(var(--text-muted))] mt-0.5">{n.time}</p>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="px-3 py-2 border-t border-[hsl(var(--border))]">
                <button className="text-xs text-[hsl(var(--accent))] hover:underline">View all notifications</button>
              </div>
            </div>
          )}
        </div>

        {/* Theme toggle */}
        <button
          onClick={onThemeToggle}
          className="p-2 rounded-md hover:bg-[hsl(var(--background))] text-[hsl(var(--text-secondary))] transition-colors"
          aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
        >
          {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
        </button>
      </header>

      {/* Command Palette */}
      {searchOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center pt-24 px-4"
          onClick={(e) => { if (e.target === e.currentTarget) setSearchOpen(false); }}
        >
          <div className="w-full max-w-lg card shadow-md animate-slide-up">
            <div className="flex items-center gap-2 px-3 py-2 border-b border-[hsl(var(--border))]">
              <Search size={14} className="text-[hsl(var(--text-muted))]" />
              <input
                ref={searchRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search vendors, contracts, issues, documents…"
                className="flex-1 bg-transparent outline-none text-sm text-[hsl(var(--text-primary))] placeholder-[hsl(var(--text-muted))]"
              />
              <button onClick={() => setSearchOpen(false)} className="text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text-primary))]">
                <X size={14} />
              </button>
            </div>
            <div className="p-2">
              <p className="section-label px-2 mb-2">Quick Navigate</p>
              <ul className="space-y-0.5">
                {QUICK_ACTIONS.map((qa) => (
                  <li key={qa.key}>
                    <Link
                      href={qa.href}
                      onClick={() => setSearchOpen(false)}
                      className="flex items-center gap-2.5 px-2.5 py-2 rounded-md hover:bg-[hsl(var(--background))] transition-colors"
                    >
                      <span className="text-base leading-none">{qa.icon}</span>
                      <span className="text-sm text-[hsl(var(--text-primary))]">{qa.label}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </>
  );
}